import React from 'react';

import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { ImagePlaceHolder } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Media from 'components/Media';
import PhotoGalleryDetail from 'components/PhotoGalleryDetail';
import Swiper from 'components/Swiper';

import { GALLERY_INDEX_QUERY, GALLERY_QUERY, GALLERY_QUERY_VALUE } from 'constants/gallery';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import useQueryState from 'hooks/useQueryState';
import useQueryToggle from 'hooks/useQueryToggle';
import type { Media as MediaType } from 'ProviderInformation-Types';

import styles from './gallerySlider.module.scss';

type Props = {
  className?: string;
  /** String to define media custom className */
  mediaClassName?: string;
  /** String to define video custom className */
  videoClassName?: string;
  /** Media list to be displayed */
  media?: Array<MediaType>;
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  /** Media list to be displayed */
  mediaSizes?: string;
  /** Flag to define whether the gallery will be displayed after clicking media item or not */
  isShowGalleryOnClick?: boolean;
};

function GallerySlider(props: Props) {
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;

  const [initImageIndex, setInitImageIndex] = useQueryState(GALLERY_INDEX_QUERY, {
    defaultValue: 0,
    parser: parseInt,
  });

  const { isOpen, onOpen, onClose } = useQueryToggle(GALLERY_QUERY, GALLERY_QUERY_VALUE.Slider);

  const handleOnOpenGallery = (index: number) => () => {
    if (props.isShowGalleryOnClick) {
      onOpen();
      setInitImageIndex(index);
    }
  };

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[props.onPage];

  return (
    <>
      <div className={props.className} data-testid="gallery-slider-wrapper">
        {isNotEmptyArray(props.media) ? (
          <Swiper
            className={styles.slider}
            paginationType={isSP ? 'swipe' : 'arrows'}
            shouldNotSwipe={!isSP}
            shouldRenderAllChild={false}
          >
            {props.media?.map((medium, index) => {
              const isVideo = medium?.type?.toLowerCase() === 'video';

              return (
                <Media
                  key={`${medium.url}-${index}`}
                  className={cx(props.mediaClassName, isVideo && props.videoClassName)}
                  media={medium}
                  isPlayableVideo={isVideo}
                  onClick={props.isShowGalleryOnClick ? handleOnOpenGallery(index) : undefined}
                  sizes={props.mediaSizes}
                  isDisableLazyload
                />
              );
            })}
          </Swiper>
        ) : (
          <ImagePlaceHolder className={props.mediaClassName} />
        )}
      </div>

      {isNotEmptyArray(props.media) && isOpen && (
        <>
          <PhotoGalleryDetail
            isOpen={isOpen}
            onClose={onClose}
            imageIndex={initImageIndex}
            mediaData={props.media}
            onChangeIndex={setInitImageIndex}
          />
          <DataLayer
            pageName={`${parentName}:hotel_photo_gallery_popup`}
            pageType={pageType}
            siteSection={siteSection}
          />
        </>
      )}
    </>
  );
}

export default GallerySlider;
